import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { DisabledAccountRedirectService } from '@platform-frontend/account';
import { AssetState, CheckoutFlow, FlowStartupService, RefreshCheckoutMessageParams } from '@platform-frontend/checkout';
import { InvalidStateError, SimpleRouterState } from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { Observable, of, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class WebCheckoutFlowStartupService {
  constructor(private disabledAccountRedirectService: DisabledAccountRedirectService, private flowStartupService: FlowStartupService, private store: Store) {}

  getStartupObservable<T extends CheckoutFlow>(
    route: ActivatedRouteSnapshot,
    checkoutFlow: (startParams: RefreshCheckoutMessageParams) => T,
    switchFlowIfNecessary: (startParams: RefreshCheckoutMessageParams, startParamsWithAssetContractId: RefreshCheckoutMessageParams) => Observable<boolean>,
    clearNotificationsForDisabledUser?: boolean
  ): Observable<unknown> {
    if (this.store.selectSnapshot(SimpleRouterState.guardsActivated)) {
      const startParamsBase64 = route.paramMap.get('startParams');
      if (!startParamsBase64) {
        return throwError(() => new InvalidStateError('No startParams provided'));
      }

      const startParams = JSON.parse(Buffer.from(startParamsBase64, 'base64').toString()) as RefreshCheckoutMessageParams;
      const flow: T = checkoutFlow(startParams);

      return this.flowStartupService.getStartupObservable<T>(flow, () =>
        this.preventStartFlow(
          () => switchFlowIfNecessary(startParams, { ...startParams, contractId: this.store.selectSnapshot(AssetState.contractId) }),
          clearNotificationsForDisabledUser
        )
      );
    }
    return of(null);
  }

  private preventStartFlow(switchFlowIfNecessary: () => Observable<boolean>, clearNotificationsForDisabledUser?: boolean): Observable<boolean> {
    return switchFlowIfNecessary().pipe(
      switchMap(switchFlow => (switchFlow ? of(true) : this.disabledAccountRedirectService.redirectDisabledUser(clearNotificationsForDisabledUser)))
    );
  }
}
